import React, {useEffect, useRef} from 'react';

const SCRIPT_ID = 'google-identity-services';
const SCRIPT_URL = 'https://accounts.google.com/gsi/client';

const loadGoogleIdentityServices = () => new Promise((resolve, reject) => {
  if (window.google?.accounts?.id) return resolve(window.google);

  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    existing.addEventListener('load', () => resolve(window.google), {once: true});
    existing.addEventListener('error', () => reject(new Error('Unable to load Google Sign-In.')), {once: true});
    return;
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_URL;
  script.async = true;
  script.defer = true;
  script.onload = () => resolve(window.google);
  script.onerror = () => reject(new Error('Unable to load Google Sign-In.'));
  document.head.appendChild(script);
});

export default function GoogleSignInButton({onCredential, onError, disabled}) {
  const shellRef = useRef(null);
  const buttonRef = useRef(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    let active = true;
    let resizeObserver;

    if (!clientId) {
      onError('Google Sign-In is not configured.');
      return undefined;
    }

    const initialise = async () => {
      try {
        const google = await loadGoogleIdentityServices();
        if (!active || !buttonRef.current) return;

        google.accounts.id.initialize({
          client_id: clientId,
          callback: response => response?.credential
            ? onCredential(response.credential)
            : onError('Google Sign-In did not return a valid credential.'),
          context: 'signin',
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        const renderButton = () => {
          if (!active || !buttonRef.current) return;
          const availableWidth = shellRef.current?.clientWidth || 220;
          const width = Math.max(220, Math.min(400, availableWidth));
          buttonRef.current.replaceChildren();
          google.accounts.id.renderButton(buttonRef.current, {
            type: 'standard', theme: 'outline', size: 'large', text: 'continue_with',
            shape: 'pill', logo_alignment: 'left', width,
          });
        };

        renderButton();
        if (window.ResizeObserver) {
          resizeObserver = new ResizeObserver(renderButton);
          resizeObserver.observe(shellRef.current);
        }
      } catch (error) {
        if (active) onError(error.message || 'Unable to load Google Sign-In.');
      }
    };

    initialise();
    return () => {
      active = false;
      resizeObserver?.disconnect();
    };
  }, [clientId, onCredential, onError]);

  return (
    <div ref={shellRef} aria-label="Continue with Google" style={{
      width: '100%', minWidth: 0, minHeight: 44, display: 'flex', justifyContent: 'center',
      overflow: 'hidden',
      opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto',
    }}>
      <div ref={buttonRef} />
    </div>
  );
}
